import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext, Link, useSearchParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { useSeo, trackFaq } from "@/lib/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Search, CalendarHeart, Phone, MessageCircle, HelpCircle, ArrowRight, ShieldCheck, Loader2,
} from "lucide-react";

const CONTAINER = "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

const FaqAccordionItem = ({ it, laneSlug }) => {
  const ls = laneSlug(it.related_service_lane);
  return (
    <AccordionItem key={it.id} value={it.slug} data-testid={`faq-item-${it.slug}`} className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
      <AccordionTrigger className="text-left text-[16px] font-semibold text-[#1C4358] hover:no-underline py-4">{it.question}</AccordionTrigger>
      <AccordionContent className="text-[15px] text-[#252221] leading-relaxed pb-4">
        <p>{it.answer}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[13px]">
          <Link to={`/resources/faq/${it.slug}`} className="text-[#9C1E22] font-semibold hover:underline">Full answer →</Link>
          {ls && <Link to={`/services/${ls}`} onClick={() => trackFaq(publicApi, { event: "faq_to_service_lane" })} className="inline-flex items-center gap-1 text-[#1C4358] hover:underline">Related service <ArrowRight size={12} /></Link>}
          {it.related_reverse_topic && <Link to={`/find/${it.related_reverse_topic}`} className="inline-flex items-center gap-1 text-[#1C4358] hover:underline">Learn more <ArrowRight size={12} /></Link>}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default function FamilyFaq() {
  const { content, openChat } = useOutletContext();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const category = sp.get("category") || "all";
  const franchise = sp.get("franchise") || null;
  const debounce = useRef(null);
  const phone = data?.local?.phone || content?.phone;

  const faqJsonLd = (data?.items || []).length ? [{
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: (data.items || []).map((it) => ({
      "@type": "Question", name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  }] : undefined;
  useSeo({
    title: "Family FAQ — Senior Move Questions Answered | MovingMyMama",
    description: "Search warm, safe answers about selling a parent's home, senior living, downsizing, and keeping your loved one safe at home. You don't have to figure this out alone.",
    canonical: typeof window !== "undefined" ? `${window.location.origin}/resources/faq` : undefined,
    jsonLd: faqJsonLd,
  });

  useEffect(() => { publicApi.get("/public/service-lanes").then(({ data }) => setLanes(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { trackFaq(publicApi, { event: "page_view", slug: "faq", franchise }); }, []); // eslint-disable-line

  const load = useCallback(async (query, cat) => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (cat && cat !== "all") params.category = cat;
      if (franchise) params.franchise = franchise;
      const { data } = await publicApi.get("/public/faq", { params });
      setData(data);
      if (query && query.length >= 3) trackFaq(publicApi, { event: "search", query, results: data.total, franchise });
    } catch { setData({ items: [], categories: [] }); }
    finally { setLoading(false); }
  }, [franchise]);

  useEffect(() => { load(sp.get("q") || "", category); }, [category]); // eslint-disable-line
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      // Read the freshest category from the live URL so a rapid "All -> type" can't reapply a stale category.
      const cur = new URLSearchParams(window.location.search);
      const curCat = cur.get("category") || "all";
      if (q) cur.set("q", q); else cur.delete("q");
      setSp(cur, { replace: true });
      load(q, curCat);
    }, 450);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q]); // eslint-disable-line

  const setCategory = (key) => { const next = new URLSearchParams(sp); if (key === "all") next.delete("category"); else next.set("category", key); setSp(next); if (key !== "all") trackFaq(publicApi, { event: "category_click", category: key }); };
  const laneSlug = (si) => lanes.find((l) => l.service_interest === si)?.slug;

  const cats = data?.categories || [];
  const items = data?.items || [];

  return (
    <div data-testid="family-faq-page" className="pb-28 lg:pb-16">
      {/* Hero */}
      <section className="relative" style={{ background: "linear-gradient(135deg,#1C4358 0%,#22536B 100%)" }}>
        <div className={`${CONTAINER} relative py-16 text-center`}>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/12 px-4 py-1.5 text-[13px] font-semibold text-white/90 mb-3"><HelpCircle size={15} /> Family FAQ</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-white">You do not have to figure this out alone</h1>
          <p className="mt-3 text-[17px] text-white/85 max-w-2xl mx-auto">Search common questions or browse by topic. When you're ready, we're right here.</p>
          <div className="mt-6 max-w-xl mx-auto relative">
            <Search size={18} className="absolute left-3.5 top-3.5 text-[#4B5563]" />
            <input data-testid="faq-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…"
              className="w-full h-13 py-3.5 pl-11 pr-4 rounded-full text-[16px] bg-white text-[#252221] outline-none" />
          </div>
        </div>
      </section>

      {/* Local routing note */}
      {data?.local && (
        <div className="bg-[#FBF6EC]"><div className={`${CONTAINER} py-3 text-center text-[14px] text-[#1C4358]`} data-testid="faq-local-note">
          Serving <span className="font-semibold">{data.local.service_area || data.local.name}</span>{data.local.phone ? <> · <a href={telHref(data.local.phone)} className="font-semibold underline">{data.local.phone}</a></> : null}
        </div></div>
      )}

      {/* Category filters */}
      <section className={`${CONTAINER} pt-8`}>
        <div className="flex flex-wrap gap-2" data-testid="faq-category-filters">
          <button data-testid="faq-cat-all" onClick={() => setCategory("all")} className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${category === "all" ? "border-[#1C4358] bg-[#1C4358] text-white" : "border-[#1C4358]/20 text-[#1C4358] hover:bg-[#1C4358]/6"}`}>All</button>
          {cats.filter((c) => c.count > 0).map((c) => (
            <button key={c.key} data-testid={`faq-cat-${c.key}`} onClick={() => setCategory(c.key)} className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${category === c.key ? "border-[#1C4358] bg-[#1C4358] text-white" : "border-[#1C4358]/20 text-[#1C4358] hover:bg-[#1C4358]/6"}`}>{c.label} <span className="opacity-70">{c.count}</span></button>
          ))}
        </div>
      </section>

      {/* FAQ list */}
      <section className={`${CONTAINER} py-6`}>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" size={28} /></div> :
          items.length === 0 ? (
            <div className="text-center py-14" data-testid="faq-empty">
              <HelpCircle className="mx-auto mb-2 text-[#9AA5B1]" />
              <p className="text-[16px] text-[#4B5563]">We don't have that answer yet — but we'd love to help your family directly.</p>
              <button type="button" onClick={() => { trackFaq(publicApi, { event: "faq_to_chat" }); openChat && openChat({ cta_source: "FAQ Chat" }); }} className="mmm-tap mt-4 inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[#9C1E22] text-white font-semibold"><MessageCircle size={17} /> Ask us directly</button>
            </div>
          ) : (category === "all" && !q) ? (
            <div className="space-y-10" data-testid="faq-grouped">
              {cats.filter((c) => c.count > 0).map((c) => {
                const groupItems = items.filter((it) => it.category === c.key);
                if (!groupItems.length) return null;
                return (
                  <div key={c.key} data-testid={`faq-group-${c.key}`}>
                    <h2 className="text-lg font-display font-bold text-[#1C4358] mb-3">{c.label}</h2>
                    <Accordion type="single" collapsible className="space-y-2"
                      onValueChange={(v) => { const it = groupItems.find((x) => x.slug === v); if (it) trackFaq(publicApi, { event: "question_view", slug: it.slug, faq_id: it.id }); }}>
                      {groupItems.map((it) => <FaqAccordionItem key={it.id} it={it} laneSlug={laneSlug} />)}
                    </Accordion>
                  </div>
                );
              })}
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion"
              onValueChange={(v) => { const it = items.find((x) => x.slug === v); if (it) trackFaq(publicApi, { event: "question_view", slug: it.slug, faq_id: it.id }); }}>
              {items.map((it) => <FaqAccordionItem key={it.id} it={it} laneSlug={laneSlug} />)}
            </Accordion>
          )}
      </section>

      {/* Safety line */}
      <div className={`${CONTAINER}`}>
        <p className="flex items-center justify-center gap-1.5 text-[12px] text-[#4B5563]"><ShieldCheck size={13} className="text-[#2F7D5A]" /> These answers are general guidance. A qualified professional should review your family's specific situation.</p>
      </div>

      {/* CTA */}
      <section className={`${CONTAINER} py-12 text-center`}>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/book" data-testid="faq-book-btn" onClick={() => trackFaq(publicApi, { event: "faq_to_booking" })} className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold"><CalendarHeart size={18} /> Book Free Consultation</Link>
          {phone && <a href={telHref(phone)} className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold hover:bg-[#1C4358]/6"><Phone size={18} /> {phone}</a>}
          <button type="button" onClick={() => { trackFaq(publicApi, { event: "faq_to_chat" }); openChat && openChat({ cta_source: "FAQ Chat" }); }} data-testid="faq-chat-btn" className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold hover:bg-[#1C4358]/6"><MessageCircle size={18} /> Start Live Chat</button>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E6EDF2] px-3 py-2 flex gap-2" data-testid="faq-sticky-cta">
        {phone && <a href={telHref(phone)} className="flex-1 mmm-tap inline-flex items-center justify-center gap-1.5 h-11 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold text-[14px]"><Phone size={16} /> Call</a>}
        <button type="button" onClick={() => { trackFaq(publicApi, { event: "faq_to_chat" }); openChat && openChat({ cta_source: "FAQ Chat" }); }} className="flex-1 mmm-tap inline-flex items-center justify-center gap-1.5 h-11 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold text-[14px]"><MessageCircle size={16} /> Chat</button>
        <Link to="/book" onClick={() => trackFaq(publicApi, { event: "faq_to_booking" })} className="flex-[1.3] mmm-tap inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[#9C1E22] text-white font-semibold text-[14px]"><CalendarHeart size={16} /> Free Consult</Link>
      </div>
    </div>
  );
}
